/**
 * Issue #344 — the Identity & Vision mount race.
 *
 * `PlayerIdentity.jsx` renders an editable form immediately and fetches
 * `/players/me/identity` on mount. Those two things finish in whichever
 * order the network decides. Before this module the fetch's `.then` called
 * `setData` with the server's answer *wholesale*, so a player who started
 * typing before the response landed had the typing replaced by whatever the
 * server held (usually nothing) — and because `PUT /players/me/identity`
 * replaces a whole section, the save that followed wrote the emptied
 * section back and showed a "saved" toast over the top of it. Silent data
 * loss, confirmed by a success message: the worst shape a bug can take.
 *
 * The fix is to treat the two sources as what they are — the server owns
 * the fields the player has not touched, the player owns the fields they
 * have — and merge on that boundary rather than letting the later arrival
 * win. `mergeLoadedIdentity` is that merge, kept pure and outside the
 * component so it is checkable per-commit (this repo mounts no components
 * in unit tests — see `LearningHub.test.js` for the standing reason).
 *
 * A "field" here is one key of one section, addressed as
 * `"<section>.<key>"` — `"vision.why_play"`, `"vision.top_ambitions"`. The
 * array-valued fields (`top_ambitions`, `preferred_formats`) are one field
 * each and dirty as a unit; a player who has set ambition 2 owns the whole
 * list, because the alternative is merging index-by-index against a server
 * list of a different length, which has no correct answer.
 */

/**
 * Merge a loaded identity payload into the draft the player is editing,
 * leaving every field they have already touched exactly as they typed it.
 *
 * @param {object} args
 * @param {object} args.blank   the empty template — its keys define which
 *                              sections and fields this page owns at all
 *                              (Mental Performance is deliberately absent;
 *                              it lives under Reflections since #214)
 * @param {object} args.current the draft as it stands right now
 * @param {object} args.loaded  the server's payload (`r.data`), possibly
 *                              partial, possibly missing sections
 * @param {{has: (k: string) => boolean}} args.dirty  the touched-field set
 * @returns {object} a new draft — never a mutation of `current`
 */
export function mergeLoadedIdentity({ blank, current, loaded, dirty }) {
  const next = {};
  for (const section of Object.keys(blank)) {
    // Same base as before #344: the blank template under the server's
    // answer, so a section the server has never stored still renders every
    // control as a controlled input rather than flipping to uncontrolled.
    const merged = { ...blank[section], ...((loaded && loaded[section]) || {}) };
    for (const key of Object.keys(blank[section])) {
      if (dirty.has(`${section}.${key}`)) {
        merged[key] = current[section][key];
      }
    }
    next[section] = merged;
  }
  return next;
}

/** The address `mergeLoadedIdentity` records a touched field under. */
export function fieldKey(section, key) {
  return `${section}.${key}`;
}
